import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Button } from '@onbeam/ui';
import { ConnectWalletButtonProps } from './ConnectWalletButton.types';
import { formatAddress, truncateText } from '@onbeam/utils';
import { defaultProps } from './ConnectWalletButton.utils';

/**
 * ConnectWalletButton component renders a button that allows users to connect their wallet (using RainbowKit).
 *
 * @param i18n - An internationalization object.
 * @param {number} maxEnsLength - Maximum length of the ENS name to display. Defaults to 15.
 * @param {boolean} isHiddenIfNotConnected - If true, hides the button when the user is not connected. Defaults to false.
 * @param {ReactNode} renderWhenConnected - A ReactNode or a render function (contains all RainbowKit its renderProps) to display a custom button when the user is connected.
 * @param {ConnectWalletButtonProps} props - Additional props to pass to the button component.
 *
 * @returns {JSX.Element | null} - Returns a button element or null based on the connection status.
 *
 * The component uses the ConnectButton.Custom component to handle the connection logic.
 * It displays different button states based on the connection status:
 * - If not connected, it shows a button to open the connect modal.
 * - If connected but the network is unsupported, it shows a button to open the chain modal.
 * - If connected and the network is supported, it shows a button to open the account modal with the user's ENS name or address.
 */
export const ConnectWalletButton = ({
  maxEnsLength = 15,
  isHiddenIfNotConnected = false,
  renderWhenConnected,
  ignoreUnsupported = false,
  ...props
}: ConnectWalletButtonProps) => {
  const i18n = {
    ...defaultProps.i18n,
    ...props.i18n,
  };

  return (
    <ConnectButton.Custom>
      {({
        account,
        chain,
        openAccountModal,
        openChainModal,
        openConnectModal,
        authenticationStatus,
        mounted,
        ...renderProps
      }) => {
        const ready = mounted && authenticationStatus !== 'loading';
        const connected =
          ready &&
          account &&
          chain &&
          (!authenticationStatus || authenticationStatus === 'authenticated');

        if (!connected && isHiddenIfNotConnected) return null;

        if (!connected) {
          return (
            <Button
              type="button"
              onClick={openConnectModal}
              isLoading={!ready}
              {...props}
            >
              {i18n.connectLabel}
            </Button>
          );
        }

        if (chain.unsupported && !ignoreUnsupported) {
          return (
            <Button type="button" onClick={openChainModal} hasLed {...props}>
              {i18n.unsupportedNetworkLabel}
            </Button>
          );
        }

        const formattedName =
          truncateText(account.ensName || '', maxEnsLength) ||
          formatAddress(account.address);

        if (renderWhenConnected) {
          return typeof renderWhenConnected === 'function'
            ? renderWhenConnected({
                formattedName,
                account,
                chain,
                openAccountModal,
                openChainModal,
                openConnectModal,
                authenticationStatus,
                mounted,
                ...renderProps,
              })
            : renderWhenConnected;
        }

        return (
          <Button
            type="button"
            onClick={openAccountModal}
            hasLed
            ledOn
            {...props}
          >
            {formattedName}
          </Button>
        );
      }}
    </ConnectButton.Custom>
  );
};
